/** Client protocol for reviewing and applying a server-owned reviewed-unclaim task. */
export interface ReviewedUnclaimReviewController {
  prepare(): Promise<{ taskId: string; operationKey: string; planSha256: string }>;
  preview(): Promise<{ taskId: string; operationKey: string; planSha256: string; input: unknown; profiles: unknown }>;
  approve(): Promise<{ taskId: string; planSha256: string; outcome: 'approved' | 'present' }>;
  apply(): Promise<{ taskId: string; planSha256: string; outcome: 'applied' | 'present' }>;
  getState(): Readonly<ReviewedUnclaimReviewState>;
}

export interface ReviewedUnclaimReviewState {
  taskId?: string;
  preparedPlanSha256?: string;
  previewPlanSha256?: string;
  previewInput?: unknown;
  previewProfiles?: unknown;
  approvedPlanSha256?: string;
  applied?: boolean;
}

type Transport = (payload: unknown) => Promise<unknown>;
type RecordValue = Record<string, unknown>;
const isRecord = (value: unknown): value is RecordValue => !!value && typeof value === 'object' && !Array.isArray(value);
const validHash = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const validTaskId = (value: unknown): value is string => typeof value === 'string'
  && value === value.trim() && value.length > 0 && value.length <= 512
  && value !== '.' && value !== '..' && !/[\/\\\u0000-\u001f\u007f]/.test(value);
const copyJsonValue = <T>(value: T): T => {
  const encoded = JSON.stringify(value);
  if (encoded === undefined) throw new Error('Reviewed-unclaim data is not JSON-safe.');
  return JSON.parse(encoded) as T;
};
const response = (value: unknown, phase: string): RecordValue => {
  if (!isRecord(value) || value.phase !== phase) throw new Error(`Invalid reviewed-unclaim ${phase} response.`);
  return value;
};

/** Reuse this controller after transport failures so every retry keeps the same task and operation key. */
export function createReviewedUnclaimReviewController(
  transport: Transport,
  options: { taskId: string; operationKey: string; frozenInventorySha256: string }
): ReviewedUnclaimReviewController {
  if (!options || !validTaskId(options.taskId)
    || typeof options.operationKey !== 'string' || !/^[a-zA-Z0-9_-]{8,128}$/.test(options.operationKey)
    || !validHash(options.frozenInventorySha256)) {
    throw new Error('Invalid reviewed-unclaim review selection.');
  }
  const taskId = options.taskId;
  const operationKey = options.operationKey;
  const frozenInventorySha256 = options.frozenInventorySha256;
  const state: ReviewedUnclaimReviewState = {};
  const requirePrepared = (): string => {
    if (!state.preparedPlanSha256) throw new Error('Prepare the reviewed-unclaim review first.');
    return taskId;
  };

  return {
    async prepare() {
      const result = response(await transport({ phase: 'prepare', taskId, operationKey, frozenInventorySha256 }), 'prepare');
      if (result.taskId !== taskId || result.operationKey !== operationKey || !validHash(result.planSha256)) {
        throw new Error('Reviewed-unclaim prepare response does not match this operation.');
      }
      if (state.preparedPlanSha256 && state.preparedPlanSha256 !== result.planSha256) {
        throw new Error('Reviewed-unclaim prepared plan changed for this operation.');
      }
      state.taskId = taskId;
      state.preparedPlanSha256 = result.planSha256;
      return { taskId, operationKey, planSha256: result.planSha256 };
    },

    async preview() {
      const preparedTaskId = requirePrepared();
      if (state.applied) throw new Error('This reviewed-unclaim operation is already complete.');
      const result = response(await transport({ phase: 'preview', taskId: preparedTaskId }), 'preview');
      if (result.taskId !== taskId || result.operationKey !== operationKey || !validHash(result.planSha256)
        || result.validation !== 'current-state' || result.readinessVerified !== true || result.applied !== false
        || !Object.prototype.hasOwnProperty.call(result, 'input')
        || !Object.prototype.hasOwnProperty.call(result, 'profiles')) {
        throw new Error('Reviewed-unclaim preview response is stale or incomplete.');
      }
      if (result.planSha256 !== state.preparedPlanSha256) {
        state.previewPlanSha256 = undefined;
        state.previewInput = undefined;
        state.previewProfiles = undefined;
        state.approvedPlanSha256 = undefined;
        throw new Error('Reviewed-unclaim preview plan changed; start a new review.');
      }
      const input = copyJsonValue(result.input);
      const profiles = copyJsonValue(result.profiles);
      state.previewPlanSha256 = result.planSha256;
      state.previewInput = input;
      state.previewProfiles = profiles;
      state.applied = false;
      return { taskId, operationKey, planSha256: result.planSha256,
        input: copyJsonValue(input), profiles: copyJsonValue(profiles) };
    },

    async approve() {
      const approvedTaskId = requirePrepared();
      const planSha256 = state.previewPlanSha256;
      if (!planSha256) throw new Error('A successful current-state preview is required before approval.');
      const result = response(await transport({ phase: 'approve', taskId: approvedTaskId, approvedPlanSha256: planSha256 }), 'approve');
      if (result.taskId !== taskId || result.planSha256 !== planSha256
        || (result.outcome !== 'approved' && result.outcome !== 'present')) {
        throw new Error('Reviewed-unclaim approval response does not match the reviewed plan.');
      }
      state.approvedPlanSha256 = planSha256;
      return { taskId, planSha256, outcome: result.outcome };
    },

    async apply() {
      const appliedTaskId = requirePrepared();
      const planSha256 = state.approvedPlanSha256;
      if (!planSha256) throw new Error('A successful approval is required before apply.');
      if (state.applied) throw new Error('This reviewed-unclaim operation is already complete.');
      const result = response(await transport({ phase: 'apply', taskId: appliedTaskId, approvedPlanSha256: planSha256 }), 'apply');
      if (result.taskId !== taskId || result.planSha256 !== planSha256
        || (result.outcome !== 'applied' && result.outcome !== 'present')) {
        throw new Error('Reviewed-unclaim apply response does not match the approved plan.');
      }
      state.applied = true;
      return { taskId, planSha256, outcome: result.outcome };
    },

    getState() { return copyJsonValue(state); },
  };
}
